import { Grid } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { formatDecimal, isMobileDevice } from "../../../commons/utils";
import BarChart from "../../../charts/Bar";
import PieChart from "../../../charts/pie";
import moment from "moment";
import { GridCard } from "..";
import { ExCard } from "../../../charts/Card";
import AreaChart from "../../../charts/area";
import { Boxes, MapPinned, Weight } from "lucide-react";
import { DashboardPage } from "../../../components/DashboardPage";
import CircularLoading from "../../../components/CircularLoading";

export const DashPlantio = forwardRef((props: any, ref) => {
    const originData = useRef<any[]>([]);

    const [data, setData] = useState<any[]>();

    const refchartpdestino = useRef<any>(null);
    const refchartexpdedicaodia = useRef<any>(null);
    const refcharttamanho = useRef<any>(null);
    const refchartorigem = useRef<any>(null);
    const refcardareaestimada = useRef<any>(null);
    const refcardareaplanejada = useRef<any>(null);
    const refcardtotal = useRef<any>(null);
    const refcardtotalemb = useRef<any>(null);
    const refchartfazenda = useRef<any>(null);
    const refchartpopulacao = useRef<any>(null);
    const refchartprodutor = useRef<any>(null);
    const refFilter = useRef<any>({})

    useImperativeHandle(ref, () => ({
        setData
    }));

    useEffect(() => {
        if (!data) return;

        originData.current = data;
        prePopulate();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const getNextMultipleOf = (value: number, factor: number) => Math.ceil(value / factor) * factor;

    const getValue = (groupByKey: string | ((item: any) => string), item: any) => typeof groupByKey === 'function' ? groupByKey(item) : item[groupByKey] || 'Não Informado'

    const groupData = (data: any[], groupByKey: string | ((item: any) => string)) =>
        data.reduce((acc: Record<string, number>, item: any) => {
            const key = getValue(groupByKey, item);
            const value = parseFloat((item.total || 0).toFixed(2));
            acc[key] = (acc[key] || 0) + value;
            return acc;
        }, {});

    const populateChart = (refChart: any, data: any, options: { groupByKey: any; chartTitle: string; limit?: number; type?: 'Bar' | 'Pie'; labelFontSize?: string, keepOrder?: boolean, percent?: boolean }) => {
        const { groupByKey, chartTitle, limit = Infinity, type = 'Bar', labelFontSize = '16px' } = options;

        var fdataPointSelection

        if (groupByKey) {
            fdataPointSelection = (e: any, ct: any, { w }: any) => {
                const acceptValues = w.globals.selectedDataPoints[0]?.map((i: number) => w.globals.labels[i]).filter((e: any) => e)

                if (!acceptValues || acceptValues.length === 0)
                    delete refFilter.current[groupByKey]
                else
                    refFilter.current[groupByKey] = (e: any) => acceptValues.includes(getValue(groupByKey, e))

                prePopulate()
            }
        }

        const filterArr = Object.entries(refFilter.current)
        data = [...data].filter((e: any) => filterArr.every(([key, val]: any) => key === groupByKey || val(e)))

        const groupedData = groupData(data, groupByKey);

        let entries: [string, number][];

        if (!options.keepOrder) {
            if (typeof groupByKey === 'function')
                entries = Object.entries(groupedData).sort(([keyA], [keyB]) => {
                    const dateA = moment(keyA, 'MM/YYYY');
                    const dateB = moment(keyB, 'MM/YYYY');
                    return dateA.diff(dateB);
                }).slice(0, limit);
            else
                entries = Object.entries(groupedData).sort(([, a], [, b]) => b - a).slice(0, limit);
        } else
            entries = Object.entries(groupedData)

        const labels = entries.map(([key]) => key);
        var values = entries.map(([, value]) => value);

        if (options.percent) {
            const total = values.reduce((s, e) => s + e, 0)
            values = values.map(e => e / total * 100)
        }

        if (type === 'Bar') {
            refChart.current?.setOptions(labels, getNextMultipleOf(Math.max(...values), options.percent ? 5 : 1000), chartTitle, options.percent, undefined, undefined, labelFontSize, fdataPointSelection);
            refChart.current?.setSeries([{ name: chartTitle, data: values }]);
        } else if (type === 'Pie') {
            refChart.current?.setLabels(labels);
            refChart.current?.setOptions(fdataPointSelection);
            refChart.current?.setSeries(values);
        }
    };

    const setCardValues = (ref: any, value: any) => {
        ref.current?.setValue(value);
    };

    const prePopulate = () => {
        var filteredData = originData.current;

        const filterArr = Object.entries(refFilter.current)
        const cardData = filteredData.filter((e: any) => filterArr.every(([key, val]: any) => val(e)))

        const areaEstimada = cardData.reduce((sum: any, item: any) => {
            if (!item.medida) return sum

            const sementes = (-item.movestqtde) * 1000 / item.medida
            return sum + ((sementes / (item.popsementes / item.popmetroslineares)) / item.popmetroslineares)
        }, 0);

        const pls = new Set(cardData.map(e => e.pldestino))
        const plsArea = Array.from(pls).map(e => cardData.find(el => el.pldestino === e).plarea)

        setCardValues(refcardareaestimada, formatDecimal(areaEstimada));
        setCardValues(refcardareaplanejada, formatDecimal(plsArea.reduce((sum: any, val: any) => sum + val, 0)));
        setCardValues(refcardtotal, formatDecimal(cardData.reduce((sum: any, item: any) => sum - item.movestqtde, 0)));
        setCardValues(refcardtotalemb, formatDecimal(cardData.reduce((sum: any, item: any) => sum - item.movestqtdeunid, 0)));

        populateChart(refchartpdestino, filteredData.map(e => ({ ...e, total: -e.movestqtde })), {
            groupByKey: 'pldestino',
            chartTitle: 'Semente expedida por PL',
            keepOrder: true
        });

        populateChart(refchartorigem, filteredData.map(e => ({ ...e, total: -e.movestqtde })), {
            groupByKey: 'pldescricao',
            chartTitle: 'Origem da Semente',
            type: 'Pie',
        });

        populateChart(refchartfazenda, filteredData.map(e => ({ ...e, total: -e.movestqtde })), {
            groupByKey: 'fazdescricao',
            chartTitle: 'Fazenda',
            type: 'Pie',
        });

        populateChart(refchartpopulacao, filteredData.map(e => ({ ...e, total: -e.movestqtde })), {
            groupByKey: 'popdescricao',
            chartTitle: 'População do Plantio',
            type: 'Pie',
        });

        populateChart(refchartprodutor, filteredData.map(e => ({ ...e, total: -e.movestqtde })), {
            groupByKey: 'pronome',
            chartTitle: 'Produtor',
            type: 'Pie',
        });

        populateChart(refchartexpdedicaodia, filteredData.map(e => ({ ...e, total: -e.movestqtde })), {
            groupByKey: (item: any) => moment(item.movemissao).format('DD/MM/YYYY'),
            chartTitle: 'Expedição por dia',
        });

        populateChart(refcharttamanho, filteredData.map(e => ({ ...e, total: -e.movestqtde })).sort((a, b) => a.culttamsequencia - b.culttamsequencia), {
            groupByKey: 'culttamdescricao',
            chartTitle: 'Semente x Tamanho',
            keepOrder: true,
            percent: true
        });
    };

    return (
        !data ? <CircularLoading /> :
            <div style={{ padding: isMobileDevice() ? '1rem' : '1rem 4rem' }}>
                <DashboardPage name="Plantio">
                    <Grid container spacing={4}>
                        <Grid item xs={isMobileDevice() ? 12 : 4}>
                            <div style={{ height: '400px' }}>
                                <PieChart ref={refchartfazenda} title='Fazenda' />
                            </div>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 4}>
                            <div style={{ height: '400px' }}>
                                <PieChart ref={refchartprodutor} title='Produtor' />
                            </div>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 4}>
                            <div style={{ height: '400px' }}>
                                <PieChart ref={refchartpopulacao} title='População' />
                            </div>
                        </Grid>

                        <Grid item xs={isMobileDevice() ? 12 : 12}>
                            <BarChart ref={refchartpdestino} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 8}>
                            <BarChart ref={refcharttamanho} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 4}>
                            <div style={{ height: '450px' }}>
                                <PieChart ref={refchartorigem} title='Origem da Semente' />
                            </div>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 12}>
                            <Grid container spacing={2}>
                                <GridCard md={3}>
                                    <ExCard ref={refcardtotal} title='Peso Total' icon={<Weight />} />
                                </GridCard>
                                <GridCard md={3}>
                                    <ExCard ref={refcardtotalemb} title='Caixas' icon={<Boxes />} />
                                </GridCard>
                                <GridCard md={3}>
                                    <ExCard ref={refcardareaestimada} title='Área Estimada' icon={<MapPinned />} />
                                </GridCard>
                                <GridCard md={3}>
                                    <ExCard ref={refcardareaplanejada} title='Área Planejada' icon={<MapPinned />} />
                                </GridCard>
                            </Grid>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 12}>
                            <AreaChart ref={refchartexpdedicaodia} />
                        </Grid>
                    </Grid>
                </DashboardPage>
            </div>
    );
});
